import { useAuthStatus, useLogout } from '@/hooks';
import { useAppStore } from '@/stores/app.ts';
import NautSmallLogo from '@nautilusbio/nb-design-system/assets/logo-n.svg';
import NautLogo from '@nautilusbio/nb-design-system/assets/naut-logo-sm.png';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { clsx } from 'clsx';
import { ArrowLeftToLine, ArrowRightToLine } from 'lucide-react';
import React, { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'react-resizable-panels';
import { useShallow } from 'zustand/react/shallow';

import NavigationLinks from './components/NavigationLinks.tsx';
import OrgSelector from './components/OrgSelector/index.ts';
import UserMenu from './components/UserMenu.tsx';
import { SIDEBAR_COLLAPSED_SIZE, SIDEBAR_DEFAULT_SIZE, SIDEBAR_MAX_SIZE, SIDEBAR_MIN_SIZE } from './constants';
import styles from './styles.module.css';
import type { SidebarProps } from './types';

const Sidebar: FC<SidebarProps> = ({ disabled, isInteracting, links, sidebarRef }) => {
  const { user } = useAuthStatus();

  // Combine all store selectors into one for better performance
  const {
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    setIsSidebarMenuOpen,
    setIsOrgSelectorOpen,
    selectedOrg,
    setSidebarSize,
  } = useAppStore(
    useShallow(state => ({
      isSidebarCollapsed: state.isSidebarCollapsed,
      setIsSidebarCollapsed: state.setIsSidebarCollapsed,
      setIsSidebarMenuOpen: state.setIsSidebarMenuOpen,
      setIsOrgSelectorOpen: state.setIsOrgSelectorOpen,
      selectedOrg: state.selectedOrganization,
      setSidebarSize: state.setSidebarSize,
    })),
  );

  const { t } = useTranslation('logo');
  const { t: tSidebar } = useTranslation('sidebar');

  const logout = useLogout();

  // Memoize resize handler to avoid creating new function on every render
  const handleResize = useCallback(() => {
    const collapsed = sidebarRef?.current?.isCollapsed() ?? false;
    setIsSidebarCollapsed(collapsed);

    // Get current size in pixels
    const sizeResult = sidebarRef?.current?.getSize?.();

    let sizeInPx = 0;
    if (sizeResult) {
      sizeInPx = sizeResult.inPixels;
    }

    setSidebarSize(sizeInPx);
  }, [sidebarRef, setIsSidebarCollapsed, setSidebarSize]);

  const handleLogout = useCallback(() => logout.mutate(), [logout]);

  const toggleHandler = useCallback(() => {
    if (sidebarRef?.current?.isCollapsed()) {
      sidebarRef?.current?.expand();
    } else {
      sidebarRef?.current?.collapse();
    }
    setIsSidebarMenuOpen(false);
    setIsOrgSelectorOpen(false);
  }, [sidebarRef, setIsOrgSelectorOpen, setIsSidebarMenuOpen]);

  // Select icon component directly
  const ToggleIcon = isSidebarCollapsed ? ArrowRightToLine : ArrowLeftToLine;

  // Select logo source directly
  const logoSrc = isSidebarCollapsed ? NautSmallLogo : NautLogo;

  return (
    <Panel
      className={clsx(styles.sidebarContainer, 'sidebar')}
      collapsedSize={SIDEBAR_COLLAPSED_SIZE}
      collapsible
      data-testid="sidebar"
      defaultSize={SIDEBAR_DEFAULT_SIZE}
      id="sidebar-panel"
      maxSize={SIDEBAR_MAX_SIZE}
      minSize={SIDEBAR_MIN_SIZE}
      onResize={handleResize}
      panelRef={sidebarRef}
    >
      <Stack as="nav" className={clsx(styles.sidebarContent, !user && styles.sidebarNoContent)} orientation="vertical">
        <div
          className={clsx(
            styles.logoContainer,
            isSidebarCollapsed && styles.logoContainerIsCollapsed,
            isInteracting && styles.sidebarInteracting,
            'logo-contain',
          )}
        >
          <img
            alt={t('alt')}
            className={clsx(styles.sidebarLogo, isSidebarCollapsed && styles.sidebarLogoIsCollapsed)}
            src={logoSrc}
            title={t('alt')}
          />
        </div>

        {!disabled && (
          <NavigationLinks
            isInteracting={isInteracting}
            links={links}
            user={user ?? undefined}
            isSidebarCollapsed={isSidebarCollapsed}
            orgId={selectedOrg?.id}
          />
        )}
      </Stack>

      <Stack
        alignItems="center"
        className={clsx(styles.sidebarFooter, isSidebarCollapsed && styles.sidebarFooterIsCollapsed)}
        gap={isSidebarCollapsed ? undefined : 3}
        justifyContent={isSidebarCollapsed ? 'start' : 'space-between'}
        orientation="horizontal"
      >
        <UserMenu
          isLoggingOut={logout.isPending}
          isSidebarCollapsed={isSidebarCollapsed}
          onLogout={handleLogout}
          selectedOrg={selectedOrg}
        />

        {selectedOrg && !isSidebarCollapsed && <OrgSelector selectedOrg={selectedOrg} />}

        <Button
          aria-label={isSidebarCollapsed ? tSidebar('toggle.expand') : tSidebar('toggle.collapse')}
          data-testid={isSidebarCollapsed ? 'sidebar-toggle-expand-btn' : 'sidebar-toggle-collapse-btn'}
          onPress={toggleHandler}
          size="xs"
          visual="unstyled"
        >
          <ToggleIcon size={16} />
        </Button>
      </Stack>
    </Panel>
  );
};

export default Sidebar;
